import {
  useEffect,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';

interface FieldProps {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}

/** A labelled form field with an optional hint and error, wired up with aria attributes. */
export function Field({ label, error, hint, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint !== undefined ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') ||
    undefined;
  return (
    <div className={`field${className ? ` ${className}` : ''}`}>
      <label htmlFor={id}>{label}</label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint !== undefined && (
        <p id={hintId} className="hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="field-error">
          {error}
        </p>
      )}
    </div>
  );
}

type TextFieldProps = {
  label: string;
  value: string;
  onValue: (v: string) => void;
  error?: string | undefined;
  hint?: ReactNode;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'id' | 'className'>;

export function TextField({
  label,
  value,
  onValue,
  error,
  hint,
  className,
  ...rest
}: TextFieldProps) {
  return (
    <Field label={label} error={error} hint={hint} className={className}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          value={value}
          onChange={(e) => onValue(e.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </Field>
  );
}

type SelectFieldProps = {
  label: string;
  value: string;
  onValue: (v: string) => void;
  options: readonly { value: string; label: string }[];
  error?: string | undefined;
  hint?: ReactNode;
  className?: string;
} & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'value' | 'onChange' | 'id' | 'className'>;

export function SelectField({
  label,
  value,
  onValue,
  options,
  error,
  hint,
  className,
  ...rest
}: SelectFieldProps) {
  return (
    <Field label={label} error={error} hint={hint} className={className}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          value={value}
          onChange={(e) => onValue(e.target.value)}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/** A page heading that takes focus when it mounts, so a route change is announced. */
export function PageHeading({ children, level = 2 }: { children: ReactNode; level?: 1 | 2 }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const active = document.activeElement;
    // Move focus only when it is not already somewhere meaningful in the page.
    if (active === null || active === document.body || active.closest('nav, header') !== null) {
      ref.current?.focus({ preventScroll: true });
    }
  }, []);
  const Tag = level === 1 ? 'h1' : 'h2';
  return (
    <Tag ref={ref} tabIndex={-1} className="page-heading">
      {children}
    </Tag>
  );
}

export function PrintButton({ label = 'Print' }: { label?: string }) {
  return (
    <button type="button" className="button no-print" onClick={() => window.print()}>
      {label}
    </button>
  );
}

/** A horizontally scrollable, keyboard-focusable region for wide tables. */
export function TableRegion({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="table-region" role="region" aria-label={label} tabIndex={0}>
      {children}
    </div>
  );
}
